import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const TEAL_GLOW = "rgba(79,205,196,0.06)";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const TABLE_BORDER = "rgba(255,255,255,0.08)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 600, fontSize: "clamp(18px, 2.5vw, 22px)", color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.01em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children }) {
  const isExternal = href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: "0.9em", color: TEXT, background: "rgba(255,255,255,0.05)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}
function PullQuote({ children }) {
  return (
    <div style={{ borderLeft: `3px solid ${TEAL}`, paddingLeft: 20, margin: "32px 0" }}>
      <p style={{ fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.5, margin: 0 }}>{children}</p>
    </div>
  );
}
function InlineCTA({ children, href, label }) {
  return (
    <div style={{ margin: "40px 0", padding: "24px 28px", borderRadius: 14, background: TEAL_DIM, border: "1px solid rgba(78,205,196,0.25)" }}>
      <p style={{ fontFamily: "var(--body)", fontSize: 15, color: TEXT, margin: "0 0 12px", fontWeight: 600 }}>{children}</p>
      <a href={href} style={{ color: TEAL, fontFamily: "var(--body)", fontSize: 15, fontWeight: 700, textDecoration: "none" }}>
        {label} &#8594;
      </a>
    </div>
  );
}
function TLDRCard({ children }) {
  return (
    <div style={{ margin: "28px 0 36px", padding: "24px 28px", borderRadius: 14, background: TEAL_GLOW, border: `1px solid ${TABLE_BORDER}` }}>
      <div style={{ fontFamily: "var(--display)", fontSize: 12, fontWeight: 700, color: TEAL, marginBottom: 12, letterSpacing: "0.1em", textTransform: "uppercase" }}>TL;DR</div>
      {children}
    </div>
  );
}

const faqs = [
  {
    q: "What's the difference between GPTBot and OAI-SearchBot?",
    a: "GPTBot is OpenAI's training-data crawler. Its job is to collect web content for use in training future foundation models. OAI-SearchBot is OpenAI's retrieval crawler. Its job is to index pages for ChatGPT Search citations. They are separate crawlers with separate user-agents, separate IP range files, and separate robots.txt tokens. Allowing or blocking them is an independent decision.",
  },
  {
    q: "Should I block GPTBot, OAI-SearchBot, both, or neither?",
    a: "For most B2B sites the default is block GPTBot, allow OAI-SearchBot. That combination opts your content out of OpenAI's foundation-model training while keeping you eligible for ChatGPT Search citations. If you want your content used in training, allow both. If you need strict privacy or run a paywall, block both and accept that ChatGPT will not cite you.",
  },
  {
    q: "Does blocking GPTBot affect my ChatGPT Search visibility?",
    a: "No. GPTBot and OAI-SearchBot are independent crawlers. Blocking GPTBot only opts you out of training. Your content remains eligible for ChatGPT Search citations as long as OAI-SearchBot can reach it. OpenAI's own documentation confirms the two bots can be controlled independently.",
  },
  {
    q: "Does Cloudflare block GPTBot and OAI-SearchBot by default?",
    a: "Not by default on every site. Cloudflare groups AI crawlers into Search, Agent, and Training, each with its own block setting, and it announced defaults for September 15, 2026 that keep Search allowed but block Training and Agent bots on pages that show ads, for new sites and for existing free-plan sites that have not changed their settings. Even if your robots.txt allows OAI-SearchBot, a Cloudflare setting or rule can still stop the request at the edge before it reaches your origin. Check Security, then Analytics, and the Events tab for blocked requests, then review your AI bot policies under Security Settings.",
  },
  {
    q: "How do I tell from my server logs which OpenAI bot is visiting?",
    a: "GPTBot logs the user-agent string GPTBot/1.3. OAI-SearchBot logs as OAI-SearchBot/1.3 with a Chrome 131 macOS prefix. ChatGPT-User logs as ChatGPT-User/1.0. OAI-AdsBot logs as OAI-AdsBot/1.0. Verify legitimacy by reverse-resolving the source IP and confirming the hostname resolves under openai.com.",
  },
  {
    q: "Will OpenAI use my content for training if I allow GPTBot?",
    a: "Yes. OpenAI's documentation states that allowing GPTBot means your content can be used to train generative AI foundation models. Disallowing GPTBot signals your content should not be used for training. If you want ChatGPT Search visibility without contributing training data, allow OAI-SearchBot and block GPTBot.",
  },
  {
    q: "What's the safest robots.txt config for a B2B mid-market site?",
    a: "Allow OAI-SearchBot, ChatGPT-User, Claude-SearchBot, and PerplexityBot site-wide, and keep Googlebot unblocked since it powers Google AI Overviews and AI Mode alongside classic Search. Block GPTBot, ClaudeBot, Google-Extended, and CCBot site-wide. This keeps you cited across the major AI search engines while opting you out of training pipelines. Confirm the allowlist also lives at your WAF and CDN edge, since robots.txt alone does not enforce edge-layer rules.",
  },
];

const howtoSteps = [
  {
    name: "Pick the scenario that matches your stance",
    text: "Choose between four configurations: B2B default (block GPTBot, allow OAI-SearchBot), training-friendly (allow both), strict privacy (block both), or selective (block GPTBot, allow OAI-SearchBot on public paths only). Most mid-market B2B sites should use the B2B default.",
  },
  {
    name: "Write the matching robots.txt block",
    text: "Add User-agent and Allow or Disallow directives for each bot. Use the exact robots.txt tokens GPTBot and OAI-SearchBot. Group them as separate User-agent blocks rather than wildcards, since OpenAI controls each bot independently.",
  },
  {
    name: "Allowlist the allowed bots in your WAF and CDN",
    text: "Robots.txt is advisory at the origin layer. Cloudflare, Akamai, Fastly, Imperva, and DataDome can still block bots at the edge. Add explicit allowlist rules for OAI-SearchBot and any other retrieval crawler you want to allow at every edge layer that fronts your origin.",
  },
  {
    name: "Verify reachability with a free bot-access checker",
    text: "Run your domain through a free AI Bot Access Checker to confirm OAI-SearchBot and other retrieval crawlers can actually reach your pages from the public internet. Re-test after every WAF, CDN, or DNS change.",
  },
];

// ── Visual 1: 2-bot comparison matrix ────────────────────────────────
function ComparisonTable() {
  const cellBase = {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_DIM,
    lineHeight: 1.55,
    verticalAlign: "top",
    borderTop: `1px solid ${TABLE_BORDER}`,
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const headerCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    fontSize: 13,
    letterSpacing: "0.02em",
  };
  const labelCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 600,
    background: "rgba(255,255,255,0.02)",
    whiteSpace: "nowrap",
    fontSize: 13,
  };
  const monoCell = {
    ...cellBase,
    fontFamily: "var(--mono)",
    fontSize: 11,
    lineHeight: 1.4,
    wordBreak: "break-all",
  };
  const tealCell = {
    ...cellBase,
    background: TEAL_GLOW,
    color: TEAL,
    fontWeight: 600,
  };

  return (
    <div className="blog-scroll-wrap" style={{ margin: "28px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="GPTBot vs OAI-SearchBot side-by-side comparison across 8 fields including purpose, training use, ChatGPT Search citation impact, user-agent, IP file, robots.txt token, recommended B2B stance, and what happens when blocked.">
        <thead>
          <tr>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}></th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none" }}>GPTBot</th>
            <th style={{ ...headerCell, textAlign: "left", borderTop: "none", borderRight: "none", background: TEAL_GLOW, color: TEAL }}>OAI-SearchBot</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelCell}>Purpose</td>
            <td style={cellBase}>Train OpenAI's foundation models</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}>Index pages for ChatGPT Search retrieval</td>
          </tr>
          <tr>
            <td style={labelCell}>Used for model training?</td>
            <td style={cellBase}>Yes</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}>No</td>
          </tr>
          <tr>
            <td style={labelCell}>Affects ChatGPT Search citations?</td>
            <td style={cellBase}>No</td>
            <td style={{ ...tealCell, borderRight: "none" }}>Yes (entirely)</td>
          </tr>
          <tr>
            <td style={labelCell}>User-agent</td>
            <td style={monoCell}>Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko); compatible; GPTBot/1.3; +https://openai.com/gptbot</td>
            <td style={{ ...monoCell, borderRight: "none", background: TEAL_GLOW }}>Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131.0.0.0 Safari/537.36; compatible; OAI-SearchBot/1.3; +https://openai.com/searchbot</td>
          </tr>
          <tr>
            <td style={labelCell}>IP range file</td>
            <td style={cellBase}><A href="https://openai.com/gptbot.json">gptbot.json</A></td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW }}><A href="https://openai.com/searchbot.json">searchbot.json</A></td>
          </tr>
          <tr>
            <td style={labelCell}>robots.txt token</td>
            <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 12 }}>GPTBot</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW, fontFamily: "var(--mono)", fontSize: 12, color: TEAL, fontWeight: 600 }}>OAI-SearchBot</td>
          </tr>
          <tr>
            <td style={labelCell}>Recommended stance (B2B)</td>
            <td style={cellBase}>Block by default; allow if you opt into training</td>
            <td style={{ ...tealCell, borderRight: "none" }}>Allow always (or exit ChatGPT Search)</td>
          </tr>
          <tr>
            <td style={labelCell}>What happens if blocked</td>
            <td style={cellBase}>Content excluded from future training. Current ChatGPT Search citations unaffected.</td>
            <td style={{ ...cellBase, borderRight: "none", background: TEAL_GLOW, color: TEXT }}>Content removed from ChatGPT Search citations. Training inclusion unaffected.</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Visual 2: 2x2 quadrant decision matrix ──────────────────────────
function QuadrantMatrix() {
  const cellBase = {
    padding: "16px 18px",
    fontSize: 13,
    color: TEXT_DIM,
    lineHeight: 1.55,
    verticalAlign: "top",
    borderTop: `1px solid ${TABLE_BORDER}`,
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const headerCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    fontSize: 13,
    letterSpacing: "0.02em",
    textAlign: "left",
  };
  const labelCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 600,
    background: "rgba(255,255,255,0.02)",
    fontSize: 13,
  };

  return (
    <div className="blog-scroll-wrap" style={{ margin: "28px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="Four-cell decision matrix mapping GPTBot and OAI-SearchBot allow or block combinations to outcomes: Allow both equals maximum exposure, Allow GPTBot and block OAI-SearchBot equals training-only opt-in, Block GPTBot and allow OAI-SearchBot equals B2B default, Block both equals strict privacy.">
        <thead>
          <tr>
            <th style={{ ...headerCell, borderTop: "none", background: "rgba(255,255,255,0.06)" }}></th>
            <th style={{ ...headerCell, borderTop: "none" }}>Allow OAI-SearchBot<br /><span style={{ fontSize: 11, fontWeight: 400, color: TEXT_DIM }}>(eligible for ChatGPT citations)</span></th>
            <th style={{ ...headerCell, borderTop: "none", borderRight: "none" }}>Block OAI-SearchBot<br /><span style={{ fontSize: 11, fontWeight: 400, color: TEXT_DIM }}>(no ChatGPT citations)</span></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={labelCell}>Allow GPTBot<br /><span style={{ fontSize: 11, fontWeight: 400, color: TEXT_DIM }}>(in training data)</span></td>
            <td style={cellBase}>
              <div style={{ fontWeight: 700, color: TEXT, marginBottom: 6 }}>Maximum exposure</div>
              <div>Content reaches training pipelines and ChatGPT Search citations. Fit for publishers, content marketers maximizing reach, and brands without licensing concerns.</div>
            </td>
            <td style={{ ...cellBase, borderRight: "none" }}>
              <div style={{ fontWeight: 700, color: TEXT, marginBottom: 6 }}>Training-only opt-in</div>
              <div>Unusual combination. Lets OpenAI train on your content but blocks ChatGPT Search from citing it. Almost never the right choice.</div>
            </td>
          </tr>
          <tr>
            <td style={labelCell}>Block GPTBot<br /><span style={{ fontSize: 11, fontWeight: 400, color: TEXT_DIM }}>(opt out of training)</span></td>
            <td style={{ ...cellBase, background: TEAL_GLOW }}>
              <div style={{ fontWeight: 700, color: TEAL, marginBottom: 6 }}>B2B default (recommended)</div>
              <div>Opts you out of OpenAI's training while keeping you eligible for ChatGPT Search citations. Fit for most mid-market B2B SaaS, services, and content sites that want visibility without giving up training data.</div>
            </td>
            <td style={{ ...cellBase, borderRight: "none" }}>
              <div style={{ fontWeight: 700, color: TEXT, marginBottom: 6 }}>Strict privacy</div>
              <div>Exits ChatGPT entirely. Fit for paywalled publishers, regulated industries (healthcare, financial), and licensed content where every form of AI access is restricted.</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Visual 4: Server-log signal mapping ─────────────────────────────
function ServerLogTable() {
  const rows = [
    {
      signature: "GPTBot/1.3 (+https://openai.com/gptbot)",
      crawler: "GPTBot",
      meaning: "Training data collection. Block if you don't want content used for OpenAI model training. Does not affect citations.",
    },
    {
      signature: "Chrome/131 ... OAI-SearchBot/1.3 (+https://openai.com/searchbot)",
      crawler: "OAI-SearchBot",
      meaning: "Indexing for ChatGPT Search retrieval. Should always reach you. If you don't see these visits, check Cloudflare and CDN edge rules.",
    },
    {
      signature: "ChatGPT-User/1.0 (+https://openai.com/bot)",
      crawler: "ChatGPT-User",
      meaning: "A ChatGPT user explicitly asked the model to fetch this URL. Allow regardless of robots.txt; OpenAI notes rules may not apply.",
    },
    {
      signature: "OAI-AdsBot/1.0 (+https://openai.com/adsbot)",
      crawler: "OAI-AdsBot",
      meaning: "ChatGPT ad landing-page validation. Relevant only if you run ChatGPT Ads. Data is excluded from training.",
    },
    {
      signature: "ClaudeBot or anthropic-ai",
      crawler: "Anthropic ClaudeBot",
      meaning: "Anthropic's training crawler. Independent of OpenAI. Block separately if you want to opt out of Claude training.",
    },
    {
      signature: "User-agent claims OAI-SearchBot, source IP not in searchbot.json",
      crawler: "Likely spoofer",
      meaning: "OpenAI publishes searchbot.json as the list of OAI-SearchBot addresses. A request from outside it is not OpenAI's search crawler, whatever its User-Agent says.",
    },
  ];

  const cellBase = {
    padding: "12px 14px",
    fontSize: 13,
    color: TEXT_DIM,
    lineHeight: 1.55,
    verticalAlign: "top",
    borderTop: `1px solid ${TABLE_BORDER}`,
    borderRight: `1px solid ${TABLE_BORDER}`,
  };
  const headerCell = {
    ...cellBase,
    color: TEXT,
    fontWeight: 700,
    background: "rgba(255,255,255,0.04)",
    borderTop: "none",
    textAlign: "left",
  };

  return (
    <div className="blog-scroll-wrap" style={{ margin: "24px 0 36px", overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${TABLE_BORDER}`, borderRadius: 12 }}>
      <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", borderSpacing: 0, fontFamily: "var(--body)" }} aria-label="Server log signature, which crawler it identifies, and what action to take. Six rows covering GPTBot, OAI-SearchBot, ChatGPT-User, OAI-AdsBot, Anthropic ClaudeBot, and spoofer detection.">
        <thead>
          <tr>
            <th style={{ ...headerCell, width: "38%" }}>Log signature</th>
            <th style={{ ...headerCell, width: "20%" }}>Which crawler</th>
            <th style={{ ...headerCell, width: "42%", borderRight: "none" }}>What it means + action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={{ ...cellBase, fontFamily: "var(--mono)", fontSize: 11, lineHeight: 1.4, wordBreak: "break-all" }}>{r.signature}</td>
              <td style={{ ...cellBase, color: TEXT, fontWeight: 600 }}>{r.crawler}</td>
              <td style={{ ...cellBase, borderRight: "none" }}>{r.meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Content() {
  return (
    <article>
      {/* ── Intro ── */}
      <P>
        <Strong>GPTBot collects training data. OAI-SearchBot powers ChatGPT Search citations.</Strong> Most "block OpenAI bots" advice on the public web treats them as a single decision. They are not. They are two separate crawlers with two separate jobs, and blocking the wrong one quietly removes you from ChatGPT visibility while accomplishing nothing about training data.
      </P>
      <P>
        This post is the binary decision: which of OpenAI's two main crawlers should your robots.txt block, which should it allow, and what changes when you get it wrong. Four worked robots.txt scenarios, a server-log signal map, the Cloudflare edge block that can silently break both, and the AI Visibility Lift bridge that extends the same discipline to Anthropic, Google, and Perplexity crawlers.
      </P>

      <TLDRCard>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li>Block GPTBot to opt out of OpenAI's foundation-model training. Your ChatGPT Search visibility is unaffected.</Li>
          <Li>Block OAI-SearchBot to exit ChatGPT Search citations entirely. Your training inclusion is unaffected.</Li>
          <Li>The default for most B2B mid-market sites: block GPTBot, allow OAI-SearchBot. Verify the allowlist also lives at your Cloudflare or CDN edge, since robots.txt alone does not stop edge-layer bot blocking.</Li>
        </ul>
      </TLDRCard>

      {/* ── Section 1 ── */}
      <H2 id="what-they-do">What GPTBot and OAI-SearchBot actually do</H2>
      <P>
        <A href="https://developers.openai.com/api/docs/bots">OpenAI's bot documentation</A> describes each crawler plainly. The short version:
      </P>
      <H3>GPTBot</H3>
      <P>
        GPTBot is OpenAI's training-data crawler. OpenAI's own description: <em>"GPTBot is used to make our generative AI foundation models more useful and safe."</em> The bot collects publicly accessible content for training future versions of models behind ChatGPT and OpenAI's other products. The user-agent prefix is <Code>GPTBot/1.3</Code>. The IP range file lives at <A href="https://openai.com/gptbot.json">openai.com/gptbot.json</A>. The robots.txt token is <Code>GPTBot</Code>.
      </P>
      <P>
        The training-data signal is explicit. From OpenAI: <em>"Disallowing GPTBot indicates a site's content should not be used in training generative AI foundation models."</em> This is the unambiguous opt-out lever for OpenAI training.
      </P>
      <H3>OAI-SearchBot</H3>
      <P>
        OAI-SearchBot is OpenAI's retrieval crawler. From the same documentation: <em>"OAI-SearchBot is used to surface websites in search results in ChatGPT's search features."</em> The bot indexes pages so ChatGPT can cite them in real-time conversation answers. The user-agent prefix is <Code>OAI-SearchBot/1.3</Code> (note the full Chrome 131 macOS prefix, the only OpenAI bot using browser-like emulation). The IP range file lives at <A href="https://openai.com/searchbot.json">openai.com/searchbot.json</A>. The robots.txt token is <Code>OAI-SearchBot</Code>.
      </P>
      <P>
        OpenAI documents the two bots as <Strong>mutually independent</Strong>. Allowing or blocking GPTBot has no effect on OAI-SearchBot's access, and vice versa. This is the architectural fact that the rest of the post depends on.
      </P>

      {/* ── Section 2 ── */}
      <H2 id="asymmetric-consequence">The asymmetric consequence: what happens when you block each one</H2>
      <P>
        The two bots have radically different costs of blocking. Get the asymmetry right and you control the trade-off precisely. Get it wrong and you pay a high price for the wrong decision.
      </P>
      <H3>Block GPTBot: low-cost opt-out</H3>
      <P>
        Blocking GPTBot opts your content out of future foundation-model training. That's it. Your existing ChatGPT Search citations are unaffected. Your eligibility for future ChatGPT Search citations is unaffected. If OpenAI's next model adds a separate retrieval crawler, the GPTBot block does not extend to it. The blast radius is narrow and predictable.
      </P>
      <H3>Block OAI-SearchBot: high-cost exit from ChatGPT visibility</H3>
      <P>
        Blocking OAI-SearchBot exits you from ChatGPT Search citations entirely. ChatGPT cannot cite a page it cannot crawl. Your content can still be in OpenAI's training data (if you didn't also block GPTBot), but ChatGPT's real-time citation pipeline is the surface that drives clicks, leads, and pipeline. Block this and you've removed yourself from the citation graph.
      </P>

      <PullQuote>
        Block GPTBot and you opt out of OpenAI training. Block OAI-SearchBot and you exit ChatGPT Search citations. Two different decisions, two different costs.
      </PullQuote>

      <P>
        The most common mistake is conflating them. Teams that want to protect their content from "AI scraping" often block both, then wonder why their <A href="/glossary/ai-citation">AI citations</A> dropped. The protection they wanted came from blocking GPTBot. Blocking OAI-SearchBot was collateral damage with no upside.
      </P>

      {/* ── Section 3: Visual 1 comparison matrix ── */}
      <H2 id="comparison">GPTBot vs OAI-SearchBot side-by-side</H2>
      <P>
        Eight fields where the two bots differ. Read across each row to see why they need separate decisions.
      </P>
      <ComparisonTable />
      <P>
        Two patterns to notice. First, the user-agent strings differ structurally: GPTBot's prefix is minimal, OAI-SearchBot's prefix includes a full Chrome 131 macOS string suggesting fuller browser-like rendering capabilities. Second, the IP range files are different (<Code>gptbot.json</Code> vs <Code>searchbot.json</Code>), which means firewall allowlists by IP must be configured for each bot independently. They cannot share a rule.
      </P>

      {/* ── Section 4: Visual 2 quadrant matrix ── */}
      <H2 id="decision-tree">The decision: which one should you block?</H2>
      <P>
        Two independent questions produce four combinations. Each combination is appropriate for a specific kind of site. Find your row in the matrix below.
      </P>
      <QuadrantMatrix />
      <P>
        The B2B default (block GPTBot, allow OAI-SearchBot) is the right starting point for most mid-market SaaS, professional services, and content-marketing sites. It opts you out of training while keeping you eligible for the citations that actually drive pipeline. The training-only opt-in quadrant is rare and almost never strategically correct. The strict-privacy quadrant fits paywalled publishers and regulated industries where every form of AI access needs to be restricted.
      </P>

      <InlineCTA href="/tools/ai-bot-checker" label="Run the free AI Bot Access Checker">
        Not sure which quadrant your site is actually in? The free AI Bot Access Checker tests your live robots.txt against 25 AI crawlers, including GPTBot and OAI-SearchBot, and flags whether your site is served through Cloudflare. Takes 60 seconds, no signup.
      </InlineCTA>

      {/* ── Section 5: Visual 3 robots.txt configs + HowTo ── */}
      <H2 id="robots-txt-configs">robots.txt configurations for 4 common scenarios</H2>
      <P>
        Each block below corresponds to one quadrant of the decision matrix. Copy the one that matches your stance, paste it into your <Code>/robots.txt</Code> file at the site root, and confirm it serves at <Code>https://yourdomain.com/robots.txt</Code> with HTTP 200.
      </P>

      <H3>Scenario 1: B2B default (recommended for most)</H3>
      <P>
        Block GPTBot, allow OAI-SearchBot. Eligible for ChatGPT Search citations, opted out of training.
      </P>
      <CodeBlock lang="robots.txt (B2B default)" syntax="text">{`# B2B default
# Block training crawler
User-agent: GPTBot
Disallow: /

# Allow retrieval crawler (for ChatGPT Search citations)
User-agent: OAI-SearchBot
Allow: /

# Allow user-initiated fetches
User-agent: ChatGPT-User
Allow: /`}</CodeBlock>

      <H3>Scenario 2: Training-friendly (publishers, max reach)</H3>
      <P>
        Allow both. Maximum exposure across training and citation surfaces.
      </P>
      <CodeBlock lang="robots.txt (training-friendly)" syntax="text">{`# Training-friendly
User-agent: GPTBot
Allow: /

User-agent: OAI-SearchBot
Allow: /

User-agent: ChatGPT-User
Allow: /`}</CodeBlock>

      <H3>Scenario 3: Strict privacy (paywalled, regulated)</H3>
      <P>
        Block both. No training, no citations.
      </P>
      <CodeBlock lang="robots.txt (strict privacy)" syntax="text">{`# Strict privacy
User-agent: GPTBot
Disallow: /

User-agent: OAI-SearchBot
Disallow: /

# Note: ChatGPT-User fires when a user explicitly fetches a URL.
# OpenAI notes robots.txt rules may not apply for this bot.`}</CodeBlock>

      <H3>Scenario 4: Selective (public pages allowed, private blocked)</H3>
      <P>
        Block GPTBot universally. Allow OAI-SearchBot on public paths, block on members-only or sensitive paths.
      </P>
      <CodeBlock lang="robots.txt (selective)" syntax="text">{`# Selective: public allowed, private blocked
User-agent: GPTBot
Disallow: /

User-agent: OAI-SearchBot
Disallow: /members/
Disallow: /account/
Disallow: /api/
Disallow: /internal/
Allow: /

User-agent: ChatGPT-User
Allow: /`}</CodeBlock>

      <P>
        After publishing your <A href="/blog/what-is-llms-txt">robots.txt and llms.txt files</A>, verify reachability at the WAF and CDN layer. Robots.txt is advisory at the origin only. The next two sections cover why that matters.
      </P>

      {/* ── Section 6: Server log signal mapping ── */}
      <H2 id="server-logs">How to identify each crawler in your server logs</H2>
      <P>
        Once your robots.txt is live, the next move is checking your server access logs for actual bot visits. Six log signatures cover the OpenAI quartet, the major Anthropic equivalent, and the spoofer pattern to watch for.
      </P>
      <ServerLogTable />
      <P>
        If your logs show GPTBot visits but no OAI-SearchBot visits, the most likely cause is a Cloudflare or CDN edge rule blocking the retrieval crawler before it reaches your origin. The next section covers that failure mode in detail. For broader tracking that captures all 16+ AI crawlers automatically, see <A href="/blog/how-to-track-ai-bot-activity">the bot activity tracking guide</A>.
      </P>

      {/* ── Section 7: Cloudflare edge-block angle ── */}
      <H2 id="cloudflare-default">The Cloudflare edge-block angle (the silent killer)</H2>
      <P>
        Cloudflare sits in front of more than 20% of web domains, and its bot settings act before a crawler ever reads robots.txt. Since July 2026, every Cloudflare site can <A href="https://developers.cloudflare.com/bots/additional-configurations/block-ai-bots/">block AI crawlers by behavior</A>: Search, Agent, or Training, on all pages or only on pages that show ads. Cloudflare <A href="https://www.cloudflare.com/press/press-releases/2026/cloudflare-allows-the-agentic-internet-to-flourish-with-a-simple-philosophy-your-content-your-rules/">announced defaults</A> for September 15, 2026 that keep Search allowed but block Training and Agent bots on ad pages for new sites and existing free-plan sites that have not changed their settings, and any setting that blocks Training also blocks crawlers that combine search and training, such as Googlebot. So a perfectly correct robots.txt can still fail to produce ChatGPT Search citations. In our <A href="/blog/are-b2b-saas-companies-blocking-ai-crawlers">September 2026 scan</A> of 390 B2B SaaS sites, 32 of the 62 that let GPTBot in by name were served through Cloudflare. Your origin says <em>allow OAI-SearchBot</em>. Your Cloudflare edge says <em>block it</em>. Cloudflare wins, the request never reaches your origin, and your ChatGPT visibility quietly dies.
      </P>

      <PullQuote>
        Your robots.txt says allow OAI-SearchBot. Your Cloudflare settings say block it. Cloudflare wins, OpenAI never reaches you, and your ChatGPT visibility quietly dies.
      </PullQuote>

      <P>
        The same problem repeats across Akamai Bot Manager, Fastly, Imperva, and DataDome. Each treats unknown bots as suspicious by default and may issue challenges, rate-limits, or outright blocks. The fix is the same at every edge layer: add an explicit allowlist rule for the retrieval crawlers you want through.
      </P>
      <H3>How to fix it in Cloudflare</H3>
      <P>
        Open the Cloudflare dashboard, go to <Strong>Security, then Analytics</Strong>, and select the <Strong>Events</Strong> tab. Look for blocked or challenged requests from <Code>OAI-SearchBot</Code>. If you find them, first check <Strong>Security Settings, then Configure AI bot policies</Strong>: Search should be allowed if you want ChatGPT Search citations. If you run Super Bot Fight Mode, you can also add a WAF custom rule that lets OAI-SearchBot skip it:
      </P>
      <CodeBlock lang="Cloudflare WAF Custom Rule (expression syntax)" syntax="text">{`# Allow OpenAI's retrieval crawler for ChatGPT Search citations
(http.user_agent contains "OAI-SearchBot/1.3")

Action: Skip
Skip:
  - All remaining custom rules
  - Super Bot Fight Mode

# Optional: log matched requests for verification
Log: Yes`}</CodeBlock>
      <P>
        Bot Fight Mode, the free version, cannot be skipped this way. <A href="https://developers.cloudflare.com/bots/get-started/bot-fight-mode/">Cloudflare says</A> Skip, Bypass, and Allow actions have no effect on it, so if Bot Fight Mode is challenging OAI-SearchBot, switch it off.
      </P>
      <P>
        If you also want training inclusion (Scenario 2 from the matrix above), repeat the rule with <Code>GPTBot/1.3</Code> as the match pattern. The principle is the same: edge-layer rules must mirror your robots.txt intent or robots.txt is silently overruled.
      </P>

      {/* ── Section 8: Common mistakes ── */}
      <H2 id="common-mistakes">Common mistakes when blocking OpenAI bots</H2>
      <P>
        Four anti-patterns surface repeatedly when teams configure crawler access for the first time. Each one breaks the binary decision in a different way.
      </P>
      <H3>Mistake 1: Blocking OAI-SearchBot thinking it's a training bot</H3>
      <P>
        OAI-SearchBot is the retrieval bot, not the training bot. Its job is to fetch pages so ChatGPT can cite them. Block this and you exit citations. If you wanted to block training data collection, you wanted to block GPTBot. The names sound similar; the consequences are opposite.
      </P>
      <H3>Mistake 2: Blocking GPTBot to "protect content" but leaving CCBot allowed</H3>
      <P>
        CCBot is Common Crawl's crawler. Common Crawl is one of the largest sources of training data for most foundation models, not just OpenAI's. Blocking GPTBot while leaving CCBot allowed is a half-measure. If your goal is to keep your content out of LLM training pipelines, block CCBot, Google-Extended, ClaudeBot, and Meta-ExternalAgent alongside GPTBot.
      </P>
      <H3>Mistake 3: Allowing OAI-SearchBot in robots.txt but not at the WAF layer</H3>
      <P>
        Robots.txt is advisory at the origin. Cloudflare, Akamai, Fastly, Imperva, and DataDome can still block bots before they reach the origin. If your robots.txt allows OAI-SearchBot but your edge layer doesn't, the edge layer wins and your content is invisible. Mirror the allowlist at every edge that fronts your origin.
      </P>
      <H3>Mistake 4: Treating ChatGPT-User the same as the other two</H3>
      <P>
        ChatGPT-User fires only when a ChatGPT user explicitly asks the model to fetch a specific URL. OpenAI's documentation notes that robots.txt rules may not apply for this bot, because the user initiated the request directly. Blocking ChatGPT-User as if it were an automated crawler breaks user-initiated browsing inside ChatGPT and accomplishes nothing about training or general citations.
      </P>

      {/* ── Section 9: AI Visibility Lift bridge ── */}
      <H2 id="ai-visibility-lift">The AI Visibility Lift: same discipline across every AI vendor</H2>
      <P>
        OpenAI's two-bot split (training vs retrieval) is not unique. Every major AI vendor separates training crawlers from retrieval crawlers from user-initiated crawlers. Once you understand the OpenAI decision, the same framework extends to Anthropic, Google, Perplexity, and Microsoft.
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>OpenAI:</Strong> GPTBot (training) and OAI-SearchBot (retrieval), plus ChatGPT-User (user-initiated) and OAI-AdsBot (ad validation, see <A href="/blog/what-is-oai-adsbot">the 4-bot taxonomy post</A>)</Li>
        <Li><Strong>Anthropic:</Strong> ClaudeBot (training) and Claude-SearchBot (retrieval), plus Claude-User (user-initiated)</Li>
        <Li><Strong>Google:</Strong> Google-Extended (a robots.txt token opting content out of AI training and grounding in Google's other systems - not a crawler) and Googlebot, which powers classic Search plus <A href="/blog/how-to-get-cited-by-google-ai-overviews">AI Overviews retrieval</A>; GoogleOther is Google's generic crawler for research and product development, not the AI Overviews agent</Li>
        <Li><Strong>Microsoft:</Strong> Bingbot powers <A href="/blog/how-to-get-cited-by-microsoft-copilot">Copilot retrieval</A> through Bing's index; no separate training bot is publicly documented</Li>
        <Li><Strong>Perplexity:</Strong> PerplexityBot (retrieval) and Perplexity-User (user-initiated)</Li>
      </ul>
      <P>
        The default discipline is the same at each vendor. Block training crawlers if you want to opt out of model training. Allow retrieval crawlers if you want to be cited in AI search answers. Allow user-initiated crawlers because the user explicitly asked. For the full allow-or-block reference across all 25 major AI crawlers, see <A href="/blog/robots-txt-ai-bots-2026">our 2026 AI crawler guide</A>. The mid-market B2B teams that apply this discipline consistently across vendors compound an AI Visibility Lift advantage that takes competitors a year to close once they realize the pattern exists.
      </P>

      <InlineCTA href="/tools/ai-bot-checker" label="Check your bot access in 60 seconds">
        The free AI Bot Access Checker tests your robots.txt against 25 AI crawlers from OpenAI, Anthropic, Google, Perplexity, and others, and flags whether your site is served through Cloudflare. No signup. Re-test after every infrastructure change. Pair with <A href="/platform/ai-analytics">AI Analytics</A> for ongoing bot-visit tracking.
      </InlineCTA>

      {/* ── HowTo JSON-LD ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "name": "How to configure robots.txt for GPTBot and OAI-SearchBot",
        "description": "A 4-step playbook for configuring your robots.txt to control OpenAI's training and retrieval crawlers independently, including the WAF and edge-layer rules that robots.txt alone does not enforce.",
        "totalTime": "PT30M",
        "step": howtoSteps.map((s, i) => ({
          "@type": "HowToStep",
          "position": i + 1,
          "name": s.name,
          "text": s.text,
        })),
      }) }} />

      {/* ── FAQPage JSON-LD ── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* ── FAQ section ── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ── Related Reading ── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-is-oai-adsbot">What Is OAI-AdsBot? (OpenAI's ChatGPT Ads Crawler)</A></Li>
        <Li><A href="/blog/how-to-track-ai-bot-activity">How to Track AI Bot Activity on Your Website</A></Li>
        <Li><A href="/blog/what-is-llms-txt">What Is llms.txt and Does Your Website Need One?</A></Li>
        <Li><A href="/blog/how-to-get-cited-by-microsoft-copilot">How to Get Cited by Microsoft Copilot: A 2026 Bing-Era SEO Playbook</A></Li>
        <Li><A href="/blog/how-to-get-cited-by-google-ai-overviews">How to Get Cited by Google AI Overviews: A 2026 Schema + Topical Authority Playbook</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "gptbot-vs-oai-searchbot",
  title: "GPTBot vs OAI-SearchBot: Why Blocking the Wrong One Kills Your ChatGPT Visibility",
  seoTitle: "GPTBot vs OAI-SearchBot: Which AI Crawler to Block",
  excerpt: "GPTBot collects training data. OAI-SearchBot powers your ChatGPT Search citations. Block the wrong one and you exit ChatGPT visibility entirely while the right one (GPTBot) only opts out of model training. Here's the binary decision, the robots.txt configurations for four real-world scenarios, and the Cloudflare edge block that can silently break both.",
  metaDescription: "GPTBot collects training data. OAI-SearchBot powers ChatGPT Search citations. Block the wrong one and you exit ChatGPT. Here's the binary decision.",
  date: "2026-05-11",
  lastUpdated: "2026-10-17",
  readTime: "11 min read",
  category: "AI Analytics",
  fiveA: "analytics",
  tags: ["GPTBot", "OAI-SearchBot", "AI crawlers", "robots.txt", "Cloudflare", "AI Analytics"],
  Content,
};

export default post;
